/**
 * UTM Parameter Tracking Utility
 * Captures and persists UTM parameters from URL for marketing attribution
 */

export interface UTMParams {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

const UTM_STORAGE_KEY = 'utm_params';

/**
 * Captures UTM parameters from current URL and stores them in sessionStorage
 * Should be called when the app loads
 */
export function captureUTMParams(): void {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams: UTMParams = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
    };

    // Only store if at least one UTM parameter exists
    const hasUTMParams = Object.values(utmParams).some(value => value !== null);
    
    if (hasUTMParams) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
      console.log('UTM parameters captured:', utmParams);
    }
  } catch (error) {
    console.error('Error capturing UTM parameters:', error);
  }
}

/**
 * Retrieves stored UTM parameters from sessionStorage
 * Returns null if no parameters are stored
 */
export function getUTMParams(): UTMParams | null {
  try {
    const storedParams = sessionStorage.getItem(UTM_STORAGE_KEY);
    
    if (!storedParams) {
      return null;
    }

    return JSON.parse(storedParams) as UTMParams;
  } catch (error) {
    console.error('Error retrieving UTM parameters:', error);
    return null;
  }
}

/**
 * Clears stored UTM parameters from sessionStorage
 */
export function clearUTMParams(): void {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing UTM parameters:', error);
  }
}
